import { cn } from '../../../../lib/utils';
import { CheckCircleFill, ChevronRight } from '@crabs-ai/web-icons';
import { cva } from 'class-variance-authority';

export interface MenuItemProps extends Omit<
  React.ComponentProps<'div'>,
  'children'
> {
  type?: 'general' | 'flyout';
  size?: 'lg' | 'md';
  showLeadingIcon?: boolean;
  showLabel?: boolean;
  icon?: React.ReactNode;
  text?: React.ReactNode;
  textProps?: React.ComponentProps<'div'>;
  subText?: string;
  subTextProps?: React.ComponentProps<'div'>;
  showSubText?: boolean;
  label?: React.ReactNode;
  isFlyoutOpen?: boolean;
  singleSelectType?: boolean;
  isSelected?: boolean;
}

const MenuItemVariants = cva(
  [
    'menu-item flex items-center gap-2xs rounded-xs justify-between cursor-pointer transition-all',
    'hover:bg-sys-bg-surface-1-hover active:bg-sys-bg-surface-1-press',
    '[&_path]:fill-sys-cont-neutral-default',
  ],
  {
    variants: {
      size: {
        lg: ['py-xs px-sm', '[&_svg]:size-[24px]'],
        md: ['py-2xs px-xs', '[&_svg]:size-[20px]'],
      },
    },
    defaultVariants: {
      size: 'lg',
    },
  },
);

const textVariants: Record<'lg' | 'md', string> = {
  lg: 'text-body-medium',
  md: 'text-body-small',
};

const subTextVariants: Record<'lg' | 'md', string> = {
  lg: 'text-caption-medium',
  md: 'text-caption-small',
};

export default function MenuItem({
  type = 'general',
  size = 'lg',
  showLeadingIcon = true,
  showSubText = true,
  showLabel = true,
  icon,
  label,
  text,
  textProps,
  subText,
  subTextProps,
  className,
  isFlyoutOpen = false,
  singleSelectType = false,
  isSelected = false,
  ...props
}: MenuItemProps) {
  const { className: textClassName, ...textRestProps } = textProps || {};
  const { className: subTextClassName, ...subTextRestProps } =
    subTextProps || {};

  return (
    <div
      className={cn(MenuItemVariants({ size }), className)}
      {...props}
    >
      <div className="flex items-center gap-2xs">
        {showLeadingIcon && icon}
        {text && (
          <div
            className={cn(
              textVariants[size],
              isSelected && singleSelectType
                ? 'text-sys-cont-primary-default'
                : 'text-sys-cont-neutral-default',
              textClassName,
            )}
            {...textRestProps}
          >
            {text}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2xs">
        {showSubText && (
          <div
            className={cn(
              subTextVariants[size],
              'text-sys-cont-neutral-lightest',
              subTextClassName,
            )}
            {...subTextRestProps}
          >
            {subText}
          </div>
        )}
        {showLabel && label && label}
        {singleSelectType && (
          <div
            className={cn(
              'transition-opacity',
              isSelected ? 'opacity-100' : 'opacity-0',
            )}
          >
            <CheckCircleFill className="[&_path]:fill-sys-cont-primary-default" />
          </div>
        )}
        {type === 'flyout' && (
          <div>
            <ChevronRight
              className={cn('transition-all', isFlyoutOpen && 'rotate-90')}
            />
          </div>
        )}
      </div>
    </div>
  );
}
